import { useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChevronDown, ArrowRight, FileText } from "lucide-react";
import { useContent } from "@/lib/content";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { getPage, pageUrl, alternatesFor, pagePath } from "@/content/seo";
import {
  orgAndWebSite,
  webPageWithLegislation,
  breadcrumbFromPath,
  formatUpdatedFR,
  CONTENT_LAST_MODIFIED,
  DIRECTIVE_APPLICABLE_SINCE,
} from "@/lib/schemaOrg";

/** Synchronise la langue de l'UI avec le préfixe d'URL de la page. */
function useRouteLang(routeLang) {
  const { lang, setLang } = useLang();
  useEffect(() => { if (routeLang && routeLang !== lang) setLang(routeLang); }, [routeLang, lang, setLang]);
  return routeLang || lang;
}
import RisksCarousel from "@/components/RisksCarousel";

// ============ MENTIONS LÉGALES ============
export function MentionsLegales() {
  const { mentions } = useContent();
  useSeoMeta({
    title: "Mentions légales — EmpCo",
    description: "Mentions légales EmpCo SAS. Éditeur, hébergement, propriété intellectuelle, données personnelles RGPD, cookies CNIL, responsabilité.",
    canonical: "https://empco-compliance.eu/mentions-legales",
    robots: "noindex, follow",
  });
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="mentions-page">
        <h1 className="text-4xl font-serif text-primary mb-8">{mentions.title}</h1>
        <div className="prose prose-slate max-w-none space-y-6 text-sm leading-relaxed">
          {mentions.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">{s.h}</h2>
              <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.html, { ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "br", "p", "ul", "ol", "li", "code"], ALLOWED_ATTR: ["href", "target", "rel"] }) }} />
            </section>
          ))}
          <p className="text-xs text-slate-500 mt-8">{mentions.lastUpdate}</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ============ FAQ ============
export function FaqPage({ lang: routeLang }) {
  const [open, setOpen] = useState({});
  const activeLang = useRouteLang(routeLang);
  const { faq } = useContent();
  const meta = getPage("faq", activeLang);
  useSeoMeta({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonical: pageUrl("faq", activeLang),
    alternates: alternatesFor("faq"),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: activeLang,
      mainEntity: (faq.sections || []).flatMap((s) => (s.items || []).map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      }))),
    },
  });
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="faq-page">
        <h1 className="text-4xl font-serif text-primary mb-2">{faq.title}</h1>
        <p className="text-slate-500 mb-10">{faq.sub}</p>
        {faq.sections.map((sec) => (
          <section key={sec.title} className="mb-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{sec.title}</h2>
            <div className="space-y-2">
              {sec.items.map((it, i) => {
                const key = `${sec.title}-${i}`;
                const isOpen = open[key];
                return (
                  <div key={key} className="border border-slate-200 rounded-lg bg-white">
                    <button onClick={() => setOpen({ ...open, [key]: !isOpen })}
                      className="w-full flex items-center justify-between p-4 text-left" data-testid={`faq-q-${sec.title.slice(0,4)}-${i}`}>
                      <span className="text-sm font-medium text-slate-900">{it.q}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{it.a}</div>}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
      <Footer />
    </div>
  );
}

// ============ LEGAL (paragraphes + carrousel amendes + bouton) ============
export function LegalPage({ lang: routeLang }) {
  const activeLang = useRouteLang(routeLang);
  const { legal } = useContent();
  const meta = getPage("law", activeLang);
  const canonical = pageUrl("law", activeLang);
  useSeoMeta({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonical,
    alternates: alternatesFor("law"),
    jsonLd: [
      orgAndWebSite(activeLang),
      webPageWithLegislation({
        url: canonical,
        name: meta.title,
        lang: activeLang,
        datePublished: "2026-02-01",
        dateModified: CONTENT_LAST_MODIFIED,
      }),
      breadcrumbFromPath(canonical.replace("https://empco-compliance.eu", ""), legal.title || "Directive", activeLang),
    ],
  });
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="legal-page">
        <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-600 font-semibold">{legal.eyebrow}</p>
        <h1 className="text-4xl md:text-5xl font-serif text-primary mt-2 mb-3">{legal.title}</h1>
        <p className="text-xs font-mono text-muted-foreground mb-8 inline-flex items-center gap-2" data-testid="legal-freshness">
          Mis à jour le {formatUpdatedFR(CONTENT_LAST_MODIFIED)} · Applicable depuis le {formatUpdatedFR(DIRECTIVE_APPLICABLE_SINCE)}
        </p>

        <div className="space-y-5 text-sm text-slate-700 leading-relaxed">
          {legal.paragraphs.map((p, i) => (
            <p key={i}><b>{p.b}</b>{p.text}</p>
          ))}
        </div>

        {/* Textes et sources cliquables (GEO Lot 2) */}
        <section className="mt-10 border-l-4 border-primary/30 pl-5" data-testid="legal-sources">
          <h2 className="text-base font-semibold text-primary mb-3">Textes officiels de référence</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><a href="https://eur-lex.europa.eu/eli/dir/2024/825/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive (UE) 2024/825 — EUR-Lex (texte officiel consolidé)</a></li>
            <li><a href="https://eur-lex.europa.eu/eli/dir/2005/29/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive 2005/29/CE sur les pratiques commerciales déloyales — EUR-Lex</a></li>
            <li><a href="https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069565/LEGISCTA000032227103/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Code de la consommation, articles L.121-1 et suivants — Légifrance</a></li>
            <li><a href="https://www.economie.gouv.fr/dgccrf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DGCCRF — Direction générale de la concurrence, de la consommation et de la répression des fraudes</a></li>
          </ul>
        </section>

        {/* Carrousel amendes */}
        <div className="mt-14 mb-10">
          <RisksCarousel />
        </div>

        <Link to={pagePath("law-text", activeLang)} data-testid="legal-learn-more"
          className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[hsl(var(--primary-light))]">
          <FileText className="w-4 h-4" /> {legal.cta}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
      <Footer />
    </div>
  );
}

// ============ Page texte officiel intégral (inchangé - source EUR-Lex française) ============
const EURLEX = {
  fr: { code: "FR", label: "eur-lex.europa.eu — CELEX:32024L0825 (version française officielle)" },
  de: { code: "DE", label: "eur-lex.europa.eu — CELEX:32024L0825 (amtliche deutsche Fassung)" },
  es: { code: "ES", label: "eur-lex.europa.eu — CELEX:32024L0825 (versión oficial en español)" },
  it: { code: "IT", label: "eur-lex.europa.eu — CELEX:32024L0825 (versione ufficiale italiana)" },
  nl: { code: "NL", label: "eur-lex.europa.eu — CELEX:32024L0825 (officiële Nederlandse versie)" },
};

const LAW_TEXT_UI = {
  fr: { back: "← Retour à la synthèse", subtitle: "du Parlement européen et du Conseil du 28 février 2024 modifiant les directives 2005/29/CE et 2011/83/UE en ce qui concerne le renforcement du rôle des consommateurs dans la transition écologique.",
    notice: "Nous ne reproduisons pas ici une traduction maison d'un texte de loi : les extraits ci-dessous renvoient à la version officielle publiée au Journal officiel de l'Union européenne.",
    h_recitals: "Considérants clés", h_art1: "Article 1er — Modifications de la directive 2005/29/CE", h_art2: "Article 2 — Modifications de la directive 2011/83/UE", h_art3: "Article 3 — Transposition",
    recitals: "Les considérants rappellent que les opérateurs économiques sont responsables de l'exactitude et de la fiabilité des allégations environnementales qu'ils formulent, et visent en particulier les allégations climatiques portant sur des performances futures (neutralité carbone à une date donnée).",
    art1: "Sont ajoutées à la liste des pratiques commerciales trompeuses : les allégations environnementales portant sur des performances futures sans engagements vérifiables ni suivi indépendant, la mise en avant d'avantages dénués de pertinence, et les allégations environnementales génériques dont l'excellence reconnue ne peut être démontrée.",
    art2: "Les informations relatives à la durabilité, à la réparabilité et à la garantie commerciale de durabilité fournie sans frais supplémentaires par le producteur deviennent obligatoires avant tout achat, en ligne comme hors ligne.",
    art3: "Publication des dispositions de transposition au plus tard le 27 mars 2026, application à partir du 27 septembre 2026.",
    footer: "Cette page présente une synthèse des articles principaux. Le texte intégral fait foi. En cas de doute juridique, consultez un avocat." },
  de: { back: "← Zurück zum Überblick", subtitle: "des Europäischen Parlaments und des Rates vom 28. Februar 2024 zur Änderung der Richtlinien 2005/29/EG und 2011/83/EU hinsichtlich der Stärkung der Verbraucher für den ökologischen Wandel.",
    notice: "Wir veröffentlichen hier keine eigene Übersetzung eines Gesetzestextes: die folgenden Auszüge verweisen auf die amtliche Fassung im Amtsblatt der Europäischen Union.",
    h_recitals: "Wichtige Erwägungsgründe", h_art1: "Artikel 1 — Änderungen der Richtlinie 2005/29/EG", h_art2: "Artikel 2 — Änderungen der Richtlinie 2011/83/EU", h_art3: "Artikel 3 — Umsetzung",
    recitals: "Die Erwägungsgründe betonen, dass Unternehmen für die Richtigkeit und Zuverlässigkeit ihrer Umweltaussagen verantwortlich sind, und nehmen insbesondere Klimaaussagen zu künftigen Leistungen in den Blick (Klimaneutralität zu einem bestimmten Datum).",
    art1: "Zur Liste irreführender Geschäftspraktiken hinzugefügt werden: Umweltaussagen zu künftigen Leistungen ohne überprüfbare Verpflichtungen und unabhängiges Monitoring, das Herausstellen irrelevanter Vorteile sowie pauschale Umweltaussagen ohne nachweisbare, anerkannte Umweltleistung.",
    art2: "Informationen zu Haltbarkeit, Reparierbarkeit und zur ohne Zusatzkosten gewährten Haltbarkeitsgarantie des Herstellers werden vor jedem Kauf verpflichtend, online wie offline.",
    art3: "Veröffentlichung der Umsetzungsvorschriften bis 27. März 2026, Anwendung ab 27. September 2026.",
    footer: "Diese Seite fasst die wichtigsten Artikel zusammen. Maßgeblich ist der amtliche Volltext. Bei Rechtsfragen ziehen Sie einen Anwalt hinzu." },
  es: { back: "← Volver a la síntesis", subtitle: "del Parlamento Europeo y del Consejo, de 28 de febrero de 2024, por la que se modifican las Directivas 2005/29/CE y 2011/83/UE en lo relativo al empoderamiento de los consumidores para la transición ecológica.",
    notice: "No publicamos aquí una traducción propia de un texto legal: los extractos remiten a la versión oficial publicada en el Diario Oficial de la Unión Europea.",
    h_recitals: "Considerandos clave", h_art1: "Artículo 1 — Modificaciones de la Directiva 2005/29/CE", h_art2: "Artículo 2 — Modificaciones de la Directiva 2011/83/UE", h_art3: "Artículo 3 — Transposición",
    recitals: "Los considerandos recuerdan que los operadores económicos son responsables de la exactitud y fiabilidad de sus alegaciones ambientales, y se centran en particular en las alegaciones climáticas sobre desempeños futuros (neutralidad en una fecha determinada).",
    art1: "Se añaden a la lista de prácticas engañosas: las alegaciones ambientales sobre desempeños futuros sin compromisos verificables ni seguimiento independiente, la publicidad de ventajas irrelevantes y las alegaciones ambientales genéricas cuya excelencia reconocida no puede demostrarse.",
    art2: "La información sobre durabilidad, reparabilidad y la garantía comercial de durabilidad ofrecida sin coste adicional por el productor pasa a ser obligatoria antes de cualquier compra, en línea y fuera de línea.",
    art3: "Publicación de las disposiciones de transposición antes del 27 de marzo de 2026 y aplicación a partir del 27 de septiembre de 2026.",
    footer: "Esta página resume los artículos principales. El texto íntegro es el que da fe. Ante dudas jurídicas, consulte a un abogado." },
  it: { back: "← Torna alla sintesi", subtitle: "del Parlamento europeo e del Consiglio, del 28 febbraio 2024, che modifica le direttive 2005/29/CE e 2011/83/UE per quanto riguarda la responsabilizzazione dei consumatori per la transizione verde.",
    notice: "Non pubblichiamo una traduzione propria di un testo di legge: gli estratti rinviano alla versione ufficiale pubblicata nella Gazzetta ufficiale dell'Unione europea.",
    h_recitals: "Considerando principali", h_art1: "Articolo 1 — Modifiche della direttiva 2005/29/CE", h_art2: "Articolo 2 — Modifiche della direttiva 2011/83/UE", h_art3: "Articolo 3 — Recepimento",
    recitals: "I considerando ricordano che gli operatori economici sono responsabili dell'esattezza e dell'affidabilità dei claim ambientali che formulano, con attenzione particolare ai claim climatici su prestazioni future (neutralità a una data determinata).",
    art1: "Sono aggiunti all'elenco delle pratiche ingannevoli: i claim ambientali su prestazioni future senza impegni verificabili né monitoraggio indipendente, la promozione di vantaggi irrilevanti e i claim ambientali generici la cui eccellenza riconosciuta non può essere dimostrata.",
    art2: "Le informazioni su durabilità, riparabilità e garanzia commerciale di durabilità offerta senza costi aggiuntivi dal produttore diventano obbligatorie prima di ogni acquisto, online e offline.",
    art3: "Pubblicazione delle disposizioni di recepimento entro il 27 marzo 2026 e applicazione dal 27 settembre 2026.",
    footer: "Questa pagina sintetizza gli articoli principali. Fa fede il testo integrale. In caso di dubbi giuridici consultate un avvocato." },
  nl: { back: "← Terug naar de samenvatting", subtitle: "van het Europees Parlement en de Raad van 28 februari 2024 tot wijziging van de Richtlijnen 2005/29/EG en 2011/83/EU wat betreft het versterken van de positie van consumenten in de groene transitie.",
    notice: "Wij publiceren hier geen eigen vertaling van een wettekst: de fragmenten verwijzen naar de officiële versie in het Publicatieblad van de Europese Unie.",
    h_recitals: "Belangrijkste overwegingen", h_art1: "Artikel 1 — Wijzigingen van Richtlijn 2005/29/EG", h_art2: "Artikel 2 — Wijzigingen van Richtlijn 2011/83/EU", h_art3: "Artikel 3 — Omzetting",
    recitals: "De overwegingen benadrukken dat ondernemingen verantwoordelijk zijn voor de juistheid en betrouwbaarheid van hun milieuclaims, met bijzondere aandacht voor klimaatclaims over toekomstige prestaties (neutraliteit op een bepaalde datum).",
    art1: "Aan de lijst met misleidende praktijken worden toegevoegd: milieuclaims over toekomstige prestaties zonder verifieerbare verplichtingen en onafhankelijke monitoring, het uitlichten van irrelevante voordelen en algemene milieuclaims waarvan de erkende uitmuntendheid niet kan worden aangetoond.",
    art2: "Informatie over levensduur, repareerbaarheid en de zonder extra kosten door de producent verstrekte commerciële duurzaamheidsgarantie wordt verplicht vóór elke aankoop, online en offline.",
    art3: "Publicatie van de omzettingsbepalingen uiterlijk 27 maart 2026 en toepassing vanaf 27 september 2026.",
    footer: "Deze pagina vat de belangrijkste artikelen samen. De officiële volledige tekst is bepalend. Raadpleeg bij juridische twijfel een advocaat." },
};

export function DirectiveFullPage({ lang: routeLang }) {
  const activeLang = useRouteLang(routeLang);
  const ui = LAW_TEXT_UI[activeLang] || LAW_TEXT_UI.fr;
  const eurlex = EURLEX[activeLang] || EURLEX.fr;
  const meta = getPage("law-text", activeLang);
  const canonical = pageUrl("law-text", activeLang);
  useSeoMeta({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
    canonical,
    alternates: alternatesFor("law-text"),
    jsonLd: [
      orgAndWebSite(activeLang),
      webPageWithLegislation({
        url: canonical,
        name: "Directive (UE) 2024/825 — texte, articles clés et calendrier",
        lang: activeLang,
        datePublished: "2026-02-01",
        dateModified: CONTENT_LAST_MODIFIED,
      }),
      breadcrumbFromPath(canonical.replace("https://empco-compliance.eu", ""), "Directive 2024/825", activeLang),
    ],
  });
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="directive-full-page">
        <Link to={pagePath("law", activeLang)} className="text-primary text-sm inline-flex items-center gap-1 mb-6">{ui.back}</Link>
        <h1 className="text-3xl font-serif text-primary mb-2">Directive (UE) 2024/825</h1>
        <p className="text-xs font-mono text-muted-foreground mb-4" data-testid="directive-freshness">
          Mis à jour le {formatUpdatedFR(CONTENT_LAST_MODIFIED)} · Applicable depuis le {formatUpdatedFR(DIRECTIVE_APPLICABLE_SINCE)}
        </p>
        <p className="text-sm text-slate-500 mb-8">{ui.subtitle}</p>
        <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-4">
          <p className="italic text-slate-500">
            {ui.notice}{" "}
            <a className="text-primary underline" target="_blank" rel="noreferrer"
              href={`https://eur-lex.europa.eu/legal-content/${eurlex.code}/TXT/?uri=CELEX:32024L0825`}>{eurlex.label}</a>.
          </p>
          <h2 className="text-lg font-semibold text-slate-900 mt-6">{ui.h_recitals}</h2>
          <p>{ui.recitals}</p>
          <h2 className="text-lg font-semibold text-slate-900 mt-6">{ui.h_art1}</h2>
          <p>{ui.art1}</p>
          <h2 className="text-lg font-semibold text-slate-900 mt-6">{ui.h_art2}</h2>
          <p>{ui.art2}</p>
          <h2 className="text-lg font-semibold text-slate-900 mt-6">{ui.h_art3}</h2>
          <p>{ui.art3}</p>
          <p className="mt-6 text-xs text-slate-500 italic">{ui.footer}</p>
        </div>

        {/* Sources cliquables (GEO Lot 2) */}
        <section className="mt-10 border-l-4 border-primary/30 pl-5" data-testid="directive-sources">
          <h2 className="text-base font-semibold text-primary mb-3">Textes et autorités officiels</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><a href="https://eur-lex.europa.eu/eli/dir/2024/825/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive (UE) 2024/825 — EUR-Lex (ELI officiel)</a></li>
            <li><a href={`https://eur-lex.europa.eu/legal-content/${eurlex.code}/TXT/?uri=CELEX:32024L0825`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Version {eurlex.code} du texte — EUR-Lex CELEX 32024L0825</a></li>
            <li><a href="https://eur-lex.europa.eu/eli/dir/2005/29/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive 2005/29/CE (pratiques commerciales déloyales) — EUR-Lex</a></li>
            <li><a href="https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069565/LEGISCTA000032227103/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Code de la consommation, articles L.121-1 et suivants — Légifrance</a></li>
            <li><a href="https://www.economie.gouv.fr/dgccrf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DGCCRF — Direction générale de la concurrence, de la consommation et de la répression des fraudes</a></li>
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
}
